import React from 'react';
import { FolderOpen, ChevronDown } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';

const DesignSelector: React.FC = () => {
  const {
    currentProject,
    getCurrentDesign,
    getDesignsForCurrentProject,
    setCurrentDesignId,
  } = useProjectStore();

  const currentDesign = getCurrentDesign();
  const availableDesigns = getDesignsForCurrentProject();

  const [isOpen, setIsOpen] = React.useState(false);

  const handleSelectDesign = (designId: string) => {
    setCurrentDesignId(designId);
    setIsOpen(false);
  };

  const handleNewDesign = () => {
    // Open the global design creation modal (handled in App)
    window.dispatchEvent(new CustomEvent('openDesignModal'));
  };

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Select Design
      </label>
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:border-blue-500 transition-colors text-left"
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <FolderOpen size={18} className="text-blue-600 flex-shrink-0" />
            {currentDesign ? (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {currentDesign.name}
                </div>
                {currentProject && (
                <div className="text-xs text-gray-500 truncate">
                    {currentProject.customer.name}
                </div>
                )}
              </div>
            ) : (
              <span className="text-sm text-gray-500">No design selected</span>
            )}
          </div>
          <ChevronDown 
            size={16} 
            className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown menu */}
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
              {availableDesigns.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No designs available. Create a design in the Designer tab.
                </div>
              ) : (
                availableDesigns.map((design) => (
                  <button
                    key={design.id}
                    onClick={() => handleSelectDesign(design.id)}
                    className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                      currentDesign?.id === design.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {design.name}
                        </div>
                        {currentProject && (
                        <div className="text-xs text-gray-500 truncate">
                            {currentProject.customer.name}
                        </div>
                        )}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {design.equipment.length} equipment • {design.cables.length} cables
                        </div>
                      </div>
                      {currentDesign?.id === design.id && (
                        <div className="ml-2 flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Simple New Design control (within current project) */}
      <button
        type="button"
        onClick={handleNewDesign}
        className="mt-3 inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
      >
        + New Design (in this project)
      </button>

      {!currentDesign && (
        <p className="mt-2 text-xs text-amber-600">
          Please select a design to generate reports/proposals
        </p>
      )}
    </div>
  );
};

export default DesignSelector;






